import { useCallback, useEffect, useRef, useState } from 'react';
import '../../backlog/backlog.css';
import './databaseErd.css';
import { api } from '../../../api/client';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';

const PAGE_ID = 'database-erd';
const OPEN_TAB_COMBO = 'alt+shift+o';

/** Fetch the ERD page. Served by the admin-or-service gated API (never a public
 *  static file — it discloses the whole database schema). */
async function fetchErdHtml(): Promise<string> {
  const res = await api.get<string>('/api/v1/database-erd', { responseType: 'text' });
  return res.data;
}

/**
 * Toolbox ▸ Database ERD — the interactive Entity Relationship Diagram of the
 * ALPACA LIMS database (tables, columns, FK relationships, data dictionary).
 * The diagram is a self-contained page rendered in a SANDBOXED frame
 * (`allow-scripts` only, no same-origin), so its script can never reach the
 * app's session or DOM.
 */
export default function DatabaseErdPage() {
  const notify = useNotify();
  const [html, setHtml] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const blobUrl = useRef<string | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    const t0 = performance.now();
    try {
      setHtml(await fetchErdHtml());
      setLoadMs(Math.round(performance.now() - t0));
    } catch {
      setHtml(null);
      setError('The database ERD could not be loaded. Administrator or service access is required.');
      notify.error('The database ERD could not be loaded.');
    } finally {
      setBusy(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => () => {
    if (blobUrl.current) URL.revokeObjectURL(blobUrl.current);
  }, []);

  const openInNewTab = useCallback(() => {
    if (!html) return;
    if (blobUrl.current) URL.revokeObjectURL(blobUrl.current);
    blobUrl.current = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    window.open(blobUrl.current, '_blank', 'noopener');
  }, [html]);

  // Standing rule 14: the bespoke toolbar button gets its key through the shared layer.
  useHotkeys([
    { combo: OPEN_TAB_COMBO, label: 'Open ERD in New Tab', group: 'Database ERD',
      run: openInNewTab, disabled: busy || !html },
  ]);

  return (
    <div className="bl-page erd-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" /><span>Toolbox</span>
            <i className="ti ti-chevron-right" /><span>Database ERD</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId={PAGE_ID} loadMs={loadMs} loading={busy} />
          </div>
          <div className="dp-subtitle">
            Tables, columns and foreign-key relationships of the ALPACA LIMS database — search a table,
            follow its relationships, or browse the Data Dictionary.
          </div>
        </div>
      </div>

      <div className="bl-grid-area erd-area">
        <AppToolbar
          left={
            <button className="ca-btn" onClick={openInNewTab} disabled={busy || !html}
              title={`Open the ERD full-screen in a new tab (${formatCombo(OPEN_TAB_COMBO)})`}>
              <i className="ti ti-external-link" /> Open in New Tab
            </button>
          }
          onRefresh={() => void load()}
          refreshing={busy}
        />
        {error ? (
          <div className="erd-empty">{error}</div>
        ) : html ? (
          <iframe className="erd-frame" title="ALPACA LIMS Entity Relationship Diagram"
            sandbox="allow-scripts" srcDoc={html} />
        ) : null}
      </div>
    </div>
  );
}
